import * as THREE from "three";
import type { VehicleDef, VehicleSeatDef } from "@hyperlight/shared";

/**
 * Lateral load (m/s²) that counts as a FULL-lean corner, and the curve applied
 * below it.
 *
 * These were far too eager at first: at a 16 m/s² threshold with a linear ramp,
 * the scout reached full lean at 6 m/s in any turn at all — its yaw rate is
 * highest exactly when it is slow, so ordinary pottering about saturated the
 * rider. A hard corner in either vehicle is ~38 m/s², so full lean now needs
 * more than that, and the exponent keeps the bottom of the range quiet: a
 * moderate turn produces a hint of weight shift rather than the full pose.
 */
const LEAN_FULL_LATERAL = 46;
const LEAN_CURVE = 2.2;
/** A full drift counts as this much extra lateral load — sliding sideways puts
 * weight through the rider the same way a corner does, but it should not pin the
 * lean on its own. */
const LEAN_DRIFT_LATERAL = 12;

/**
 * Signed cornering load for the rider's weight shift, -1 (hard left) .. 1
 * (hard right). Built from what the chassis is DOING — lateral acceleration is
 * forward speed × yaw rate — rather than from steering input, so the rider does
 * not lean while the vehicle understeers straight on.
 */
export function riderLeanSignal(forwardSpeed: number, yawRate: number, drift = 0): number {
  const lateral = forwardSpeed * yawRate + Math.sign(yawRate) * drift * LEAN_DRIFT_LATERAL;
  const normalized = Math.min(1, Math.abs(lateral) / LEAN_FULL_LATERAL);
  return Math.sign(lateral) * Math.pow(normalized, LEAN_CURVE);
}

export function driverSeat(def: VehicleDef): VehicleSeatDef | null {
  return def.seats.find((seat) => seat.role === "driver") ?? null;
}

/** Resolve any authored seat socket into simulation-world space. */
export function vehicleSeatPose(
  seat: VehicleSeatDef,
  vehiclePosition: THREE.Vector3,
  vehicleRotation: THREE.Quaternion,
  outPosition: THREE.Vector3,
  outRotation: THREE.Quaternion,
): void {
  outPosition.set(...seat.position).applyQuaternion(vehicleRotation).add(vehiclePosition);
  outRotation.copy(vehicleRotation);
}

/** Resolve a model-independent avatar root from the authored driver socket. */
export function vehicleRiderPose(
  def: VehicleDef,
  vehiclePosition: THREE.Vector3,
  vehicleRotation: THREE.Quaternion,
  outPosition: THREE.Vector3,
  outRotation: THREE.Quaternion,
): boolean {
  const seat = driverSeat(def);
  if (!seat) return false;
  vehicleSeatPose(seat, vehiclePosition, vehicleRotation, outPosition, outRotation);
  return true;
}
